import type { ConditionCode } from './contract'

/**
 * Presentation metadata for every condition code: label, icon slug, and the sky "scene"
 * the animated background and widget gradients render. Shared by the app, Widget Studio
 * and the embeddable widget so every surface agrees.
 */

export type SkyKind =
  | 'clear'
  | 'partly'
  | 'cloudy'
  | 'overcast'
  | 'fog'
  | 'haze'
  | 'rain'
  | 'storm'
  | 'snow'
  | 'sleet'

export interface ConditionMeta {
  label: string
  /** Base icon slug; see `iconSlug()` for the day/night-resolved name. */
  icon: string
  /** Whether the icon has distinct day/night variants (`-day` / `-night`). */
  dayNight: boolean
  sky: SkyKind
  /** 0–1 cloud coverage the sky renderer should draw. */
  cloud: number
  /** 0–1 precipitation intensity for particles. */
  precip: number
  precipKind: 'rain' | 'snow' | 'sleet' | 'hail' | null
  thunder: boolean
  /** 0–1 fog / haze density. */
  fog: number
}

const m = (
  label: string,
  icon: string,
  dayNight: boolean,
  sky: SkyKind,
  cloud: number,
  precip = 0,
  precipKind: ConditionMeta['precipKind'] = null,
  thunder = false,
  fog = 0,
): ConditionMeta => ({ label, icon, dayNight, sky, cloud, precip, precipKind, thunder, fog })

export const CONDITIONS: Record<ConditionCode, ConditionMeta> = {
  clear: m('Clear', 'clear', true, 'clear', 0),
  mostly_clear: m('Mostly Clear', 'mostly-clear', true, 'clear', 0.2),
  partly_cloudy: m('Partly Cloudy', 'partly-cloudy', true, 'partly', 0.45),
  mostly_cloudy: m('Mostly Cloudy', 'overcast', true, 'cloudy', 0.75),
  cloudy: m('Cloudy', 'overcast', false, 'overcast', 0.95),
  fog: m('Fog', 'fog', true, 'fog', 0.6, 0, null, false, 0.9),
  haze: m('Haze', 'haze', true, 'haze', 0.2, 0, null, false, 0.45),
  smoke: m('Smoke', 'smoke', false, 'haze', 0.3, 0, null, false, 0.6),
  dust: m('Blowing Dust', 'dust', true, 'haze', 0.25, 0, null, false, 0.55),
  breezy: m('Breezy', 'wind', false, 'partly', 0.35),
  windy: m('Windy', 'wind', false, 'partly', 0.45),
  drizzle: m('Drizzle', 'overcast-drizzle', false, 'rain', 0.9, 0.2, 'rain'),
  light_rain: m('Light Rain', 'overcast-drizzle', false, 'rain', 0.9, 0.35, 'rain'),
  rain: m('Rain', 'overcast-rain', false, 'rain', 1, 0.6, 'rain'),
  heavy_rain: m('Heavy Rain', 'extreme-rain', false, 'rain', 1, 1, 'rain'),
  showers: m('Showers', 'partly-cloudy-rain', true, 'rain', 0.8, 0.5, 'rain'),
  scattered_showers: m('Scattered Showers', 'partly-cloudy-rain', true, 'partly', 0.6, 0.3, 'rain'),
  thunderstorms: m('Thunderstorms', 'thunderstorms-rain', false, 'storm', 1, 0.8, 'rain', true),
  scattered_thunderstorms: m(
    'Scattered Thunderstorms',
    'thunderstorms-rain',
    true,
    'storm',
    0.8,
    0.5,
    'rain',
    true,
  ),
  severe_thunderstorms: m(
    'Severe Thunderstorms',
    'thunderstorms-extreme-rain',
    false,
    'storm',
    1,
    1,
    'rain',
    true,
  ),
  flurries: m('Flurries', 'overcast-snow', false, 'snow', 0.8, 0.15, 'snow'),
  light_snow: m('Light Snow', 'overcast-snow', false, 'snow', 0.9, 0.35, 'snow'),
  snow: m('Snow', 'overcast-snow', false, 'snow', 1, 0.6, 'snow'),
  heavy_snow: m('Heavy Snow', 'extreme-snow', false, 'snow', 1, 1, 'snow'),
  snow_showers: m('Snow Showers', 'partly-cloudy-snow', true, 'snow', 0.8, 0.45, 'snow'),
  blizzard: m('Blizzard', 'extreme-snow', false, 'snow', 1, 1, 'snow', false, 0.5),
  sleet: m('Sleet', 'overcast-sleet', false, 'sleet', 1, 0.5, 'sleet'),
  freezing_drizzle: m('Freezing Drizzle', 'overcast-sleet', false, 'sleet', 1, 0.25, 'sleet'),
  freezing_rain: m('Freezing Rain', 'overcast-sleet', false, 'sleet', 1, 0.55, 'sleet'),
  wintry_mix: m('Wintry Mix', 'overcast-sleet', false, 'sleet', 1, 0.5, 'sleet'),
  hail: m('Hail', 'overcast-hail', false, 'storm', 1, 0.7, 'hail', true),
  hot: m('Hot', 'sun-hot', false, 'clear', 0.05),
  frigid: m('Frigid', 'thermometer-colder', false, 'clear', 0.1),
  tropical_storm: m('Tropical Storm', 'hurricane', false, 'storm', 1, 0.9, 'rain'),
  hurricane: m('Hurricane', 'hurricane', false, 'storm', 1, 1, 'rain'),
}

/** Resolve the icon slug, adding `-day` / `-night` where the icon set has variants. */
export function iconSlug(code: ConditionCode, isDay: boolean): string {
  const meta = CONDITIONS[code] ?? CONDITIONS.cloudy
  if (!meta.dayNight) return meta.icon
  const [head, ...rest] = meta.icon.split('-')
  // "partly-cloudy-rain" -> "partly-cloudy-day-rain"; "clear" -> "clear-day"
  const suffix = isDay ? 'day' : 'night'
  if (meta.icon.startsWith('partly-cloudy')) {
    return ['partly-cloudy', suffix, ...meta.icon.split('-').slice(2)].join('-')
  }
  if (meta.icon.startsWith('mostly-clear')) {
    return ['mostly-clear', suffix, ...meta.icon.split('-').slice(2)].join('-')
  }
  if (meta.icon.startsWith('thunderstorms')) {
    return ['thunderstorms', suffix, ...rest].join('-')
  }
  return [head, suffix, ...rest].join('-')
}

export function conditionLabel(code: ConditionCode): string {
  return (CONDITIONS[code] ?? CONDITIONS.cloudy).label
}

// ─── Sky palettes ──────────────────────────────────────────────────────────────

/**
 * A palette is the gradient the sky renderer paints behind everything (top → bottom) plus
 * the translucent tint for glass cards sitting on it. Values are tuned to feel like
 * Apple Weather's condition backgrounds. `phase` blends night → twilight → day by
 * sun altitude, so callers normally use `skyPalette()`.
 */
export interface SkyPalette {
  top: string
  mid: string
  bottom: string
  /** Glass card fill (rgba). */
  card: string
  /** Hairline / divider on cards (rgba). */
  line: string
}

type PaletteSet = { day: SkyPalette; twilight: SkyPalette; night: SkyPalette }

const p = (top: string, mid: string, bottom: string, card: string, line: string): SkyPalette => ({
  top,
  mid,
  bottom,
  card,
  line,
})

export const SKY_PALETTES: Record<SkyKind, PaletteSet> = {
  clear: {
    day: p('#2d6fd0', '#4c8fe0', '#86bbef', 'rgba(24, 78, 160, 0.26)', 'rgba(255,255,255,0.22)'),
    twilight: p('#27447e', '#8a6f9e', '#f0a86e', 'rgba(40, 40, 90, 0.26)', 'rgba(255,255,255,0.2)'),
    night: p('#070b1d', '#111a3a', '#1f2c56', 'rgba(20, 28, 64, 0.34)', 'rgba(255,255,255,0.14)'),
  },
  partly: {
    day: p('#3a74c0', '#5f94d2', '#9cc0e6', 'rgba(30, 76, 140, 0.26)', 'rgba(255,255,255,0.22)'),
    twilight: p('#2f4574', '#7c6c90', '#d99c78', 'rgba(40, 44, 84, 0.28)', 'rgba(255,255,255,0.2)'),
    night: p('#0c1226', '#18223f', '#2a3658', 'rgba(22, 30, 60, 0.36)', 'rgba(255,255,255,0.14)'),
  },
  cloudy: {
    day: p('#51708f', '#7390ab', '#a3b6c8', 'rgba(40, 64, 92, 0.28)', 'rgba(255,255,255,0.2)'),
    twilight: p('#3a4760', '#6a6a82', '#a88f8a', 'rgba(40, 44, 66, 0.3)', 'rgba(255,255,255,0.18)'),
    night: p('#161c29', '#232b3b', '#343e51', 'rgba(26, 32, 46, 0.4)', 'rgba(255,255,255,0.13)'),
  },
  overcast: {
    day: p('#5d6f82', '#7d8d9d', '#a3afbb', 'rgba(46, 58, 74, 0.3)', 'rgba(255,255,255,0.2)'),
    twilight: p('#3d4555', '#5f6272', '#8d8584', 'rgba(42, 46, 60, 0.32)', 'rgba(255,255,255,0.17)'),
    night: p('#191d25', '#262b35', '#363c48', 'rgba(28, 32, 42, 0.42)', 'rgba(255,255,255,0.12)'),
  },
  fog: {
    day: p('#8e9aa6', '#a9b3bc', '#c8ced4', 'rgba(70, 82, 96, 0.3)', 'rgba(255,255,255,0.24)'),
    twilight: p('#5e6474', '#838393', '#b0a4a0', 'rgba(56, 60, 74, 0.32)', 'rgba(255,255,255,0.2)'),
    night: p('#232830', '#343a43', '#4a5059', 'rgba(34, 38, 46, 0.42)', 'rgba(255,255,255,0.13)'),
  },
  haze: {
    day: p('#8a8778', '#ab9f86', '#d2bf9c', 'rgba(92, 80, 60, 0.28)', 'rgba(255,255,255,0.22)'),
    twilight: p('#5d4f55', '#9a6f5e', '#dc9565', 'rgba(70, 50, 50, 0.3)', 'rgba(255,255,255,0.2)'),
    night: p('#1d1a1e', '#2d272a', '#433a38', 'rgba(40, 34, 34, 0.42)', 'rgba(255,255,255,0.13)'),
  },
  rain: {
    day: p('#465565', '#5d6c7b', '#7f8c98', 'rgba(34, 44, 56, 0.34)', 'rgba(255,255,255,0.18)'),
    twilight: p('#2e3546', '#474b5e', '#6a6570', 'rgba(32, 36, 50, 0.36)', 'rgba(255,255,255,0.16)'),
    night: p('#10151d', '#1b222c', '#29313c', 'rgba(20, 26, 34, 0.46)', 'rgba(255,255,255,0.12)'),
  },
  storm: {
    day: p('#2e3346', '#43475e', '#626579', 'rgba(26, 28, 44, 0.4)', 'rgba(255,255,255,0.17)'),
    twilight: p('#221f35', '#3d3550', '#5c4c62', 'rgba(26, 22, 40, 0.42)', 'rgba(255,255,255,0.15)'),
    night: p('#0b0c16', '#161726', '#242537', 'rgba(16, 16, 28, 0.5)', 'rgba(255,255,255,0.12)'),
  },
  snow: {
    day: p('#7f93ab', '#a1b1c4', '#cdd6e1', 'rgba(56, 72, 96, 0.28)', 'rgba(255,255,255,0.26)'),
    twilight: p('#4c5672', '#7b7f98', '#b3aab6', 'rgba(50, 56, 80, 0.3)', 'rgba(255,255,255,0.2)'),
    night: p('#1c2231', '#2b3346', '#414b60', 'rgba(30, 36, 52, 0.42)', 'rgba(255,255,255,0.14)'),
  },
  sleet: {
    day: p('#667587', '#8795a4', '#adb8c3', 'rgba(44, 54, 68, 0.32)', 'rgba(255,255,255,0.2)'),
    twilight: p('#3b4254', '#5c6070', '#86818a', 'rgba(38, 42, 56, 0.34)', 'rgba(255,255,255,0.17)'),
    night: p('#161a22', '#22272f', '#33393f', 'rgba(24, 28, 36, 0.46)', 'rgba(255,255,255,0.12)'),
  },
}

/**
 * Sun altitude → phase weights. Night below −8°, day above +8°, twilight in between
 * (peaking at the horizon). Returns weights that sum to 1.
 */
export function phaseWeights(sunAltitudeDeg: number): { night: number; twilight: number; day: number } {
  const a = sunAltitudeDeg
  if (a <= -8) return { night: 1, twilight: 0, day: 0 }
  if (a >= 8) return { night: 0, twilight: 0, day: 1 }
  if (a < 0) {
    const t = (a + 8) / 8
    return { night: 1 - t, twilight: t, day: 0 }
  }
  const t = a / 8
  return { night: 0, twilight: 1 - t, day: t }
}

function hexToRgb(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16)
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255]
}

function mixHex(colors: [string, number][]): string {
  const acc = [0, 0, 0]
  for (const [hex, w] of colors) {
    const [r, g, b] = hexToRgb(hex)
    acc[0] += r * w
    acc[1] += g * w
    acc[2] += b * w
  }
  return `#${acc.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`
}

function mixRgba(colors: [string, number][]): string {
  const acc = [0, 0, 0, 0]
  for (const [rgba, w] of colors) {
    const parts = rgba.match(/[\d.]+/g)?.map(Number) ?? [0, 0, 0, 0]
    for (let i = 0; i < 4; i++) acc[i] += (parts[i] ?? 0) * w
  }
  return `rgba(${Math.round(acc[0])}, ${Math.round(acc[1])}, ${Math.round(acc[2])}, ${acc[3].toFixed(3)})`
}

/** The palette for a condition at a given sun altitude, smoothly blended across twilight. */
export function skyPalette(code: ConditionCode, sunAltitudeDeg: number): SkyPalette {
  const set = SKY_PALETTES[(CONDITIONS[code] ?? CONDITIONS.cloudy).sky]
  const w = phaseWeights(sunAltitudeDeg)
  const parts: [SkyPalette, number][] = [
    [set.night, w.night],
    [set.twilight, w.twilight],
    [set.day, w.day],
  ]
  const pick = (k: keyof SkyPalette) => parts.map(([pal, wt]) => [pal[k], wt] as [string, number])
  return {
    top: mixHex(pick('top')),
    mid: mixHex(pick('mid')),
    bottom: mixHex(pick('bottom')),
    card: mixRgba(pick('card')),
    line: mixRgba(pick('line')),
  }
}

/** CSS linear-gradient for a palette — used by list cards and widgets. */
export function skyGradientCss(pal: SkyPalette, angle = 180): string {
  return `linear-gradient(${angle}deg, ${pal.top} 0%, ${pal.mid} 55%, ${pal.bottom} 100%)`
}
